// lib
import React, { useContext, useEffect, useState } from 'react';
import useSnackbar from '@joinforma/ui/hooks/useSnackbar';

// src
import PretaxContext from 'contexts/PretaxContext';
import CurrentUserContext from 'contexts/CurrentUserContext';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import { SettingsProfile } from './SettingsProfile';
import * as SettingApis from 'apis/setting';
import { isTwicCardEnabled } from 'utils/employee';
import useQueryProfile from 'hooks/useQueryProfile';
import { FeatureFlag } from 'constants/featureFlag';

const SettingsProfilePage = () => {
  const { showSuccessSnackbar, showErrorSnackbar } = useSnackbar();
  const { company, employee, refetchCurrentUser } =
    useContext(CurrentUserContext);
  const { alegeus_pretax_accounts, pretax_accounts, updateWallets } =
    useContext(PretaxContext);

  const feature_flag = useContext(FeatureFlagContext);
  const is_pretax_v2 = feature_flag?.flag(
    FeatureFlag.ALEGEUS_MIGRATED_CUSTOMER,
    false
  );

  const [uploading, setUploading] = useState(false);
  const [showAddressMessageModal, setShowAddressMessageModal] = useState(false);
  const [showPhoneNumberModal, setShowPhoneNumberModal] = useState(false);
  const [is_updating_employee, setEmployeeUpdating] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);

  const {
    company_detail,
    settings: {
      country,
      is_posttax_enabled: is_member_posttax_enabled,
      is_pretax_enabled: is_member_pretax_enabled,
    },
  } = employee;

  const has_pretax_accounts = is_pretax_v2
    ? pretax_accounts?.length > 0
    : alegeus_pretax_accounts?.length > 0;

  const is_company_pretax_enabled =
    company_detail?.features?.pretax_config?.pretax_enabled;
  const is_forma_card_enabled =
    company_detail?.settings?.stripe_physical_card_enabled ||
    company_detail?.settings?.stripe_virtual_card_enabled;

  const is_forma_card_enabled_for_member =
    is_forma_card_enabled &&
    country === 'us' &&
    company_detail?.features?.credit_card;

  const is_forma_store_enabled = company_detail?.features?.marketplace;
  const is_international_store_enabled =
    company_detail?.features?.marketplace_international;

  const is_member_us_citizen = country === 'us';

  const card_only =
    (is_forma_card_enabled_for_member &&
      is_member_posttax_enabled &&
      is_member_us_citizen) ||
    (is_company_pretax_enabled && has_pretax_accounts);

  const store_only =
    is_member_posttax_enabled &&
    ((is_forma_store_enabled && is_member_us_citizen) ||
      (is_international_store_enabled && !is_member_us_citizen));

  const card_and_store =
    is_member_posttax_enabled &&
    is_forma_store_enabled &&
    is_member_us_citizen &&
    (is_forma_card_enabled_for_member ||
      (is_company_pretax_enabled && has_pretax_accounts));

  const mailing_address_description_placeholder = card_and_store
    ? `Forma Store orders and Forma Card`
    : store_only
    ? `Forma Store orders`
    : card_only
    ? `Forma Card`
    : '';

  const show_phone = card_only || card_and_store || is_member_pretax_enabled;

  const { data: profile_data, isLoading: is_profile_v3_loading } =
    useQueryProfile({
      isEnabled: Boolean(employee.id && is_pretax_v2),
      use_v3_profile: is_pretax_v2,
    });

  useEffect(() => {
    if (is_pretax_v2 && !is_profile_v3_loading && profile_data) {
      const {
        employee: { employee_wallets: employee_wallets_v3 },
      } = profile_data;
      updateWallets(employee_wallets_v3);
    }
  }, [profile_data, is_pretax_v2, is_profile_v3_loading, updateWallets]);

  const onAvatarUpload = async (file: any) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      await SettingApis.updateProfile(formData);
      await refetchCurrentUser();
      showSuccessSnackbar({
        text: 'Your avatar has been updated.',
      });
      return true;
    } catch (e: any) {
      const {
        response: { data },
      } = e;
      const { message = 'An error occured.' } = data || {};
      showErrorSnackbar({
        text: message,
      });
    } finally {
      setUploading(false);
    }
  };

  const handleEmployeeSubmit = async ({
    preferred_first_name = '',
    preferred_last_name = '',
    personal_email = '',
    street = '',
    street_ext = '',
    locality = '',
    state = '',
    zip = '',
    country = '',
    phone,
  }: any) => {
    const updated_country = country?.toLowerCase();
    // state is not required if company is US
    const is_address_complete_or_clear =
      (street && locality && state && zip && updated_country) ||
      (street && locality && zip && updated_country !== 'us') ||
      (!street && !locality && !state && !zip && !updated_country);

    /**
     * Both international and US based customers must enter a valid address
     * when editing, but only if Forma card is enabled per MX-1076.
     */
    if (
      !is_address_complete_or_clear &&
      isTwicCardEnabled({ employee, company })
    ) {
      setShowAddressMessageModal(true);
      return;
    }

    // International members do not have phone number field
    if (!phone && is_member_us_citizen && show_phone) {
      setShowPhoneNumberModal(true);
      return;
    }
    const formData = {
      phone: phone
        ? is_member_us_citizen
          ? `+1${phone.replace(/\D/g, '')}`
          : phone
        : '',
      personal_email,
      preferred_first_name,
      preferred_last_name,
      shipping_address: {
        street,
        street_ext,
        locality,
        state,
        zip,
        country: updated_country,
      },
    };
    try {
      setEmployeeUpdating(true);
      await SettingApis.updateProfile(formData);
      await refetchCurrentUser();
      showSuccessSnackbar({
        text: 'Your profile has been updated.',
      });
    } catch (e: any) {
      const {
        response: { data },
      } = e;
      const { message = 'An error occured.' } = data || {};
      showErrorSnackbar({
        text: message,
      });
    } finally {
      setEmployeeUpdating(false);
      setShowEditForm(false);
    }
  };

  return (
    <SettingsProfile
      show_phone={!!show_phone}
      avatar_uploading={uploading}
      is_updating_employee={is_updating_employee}
      showAddressMessageModal={showAddressMessageModal}
      showPhoneNumberModal={showPhoneNumberModal}
      showEditForm={showEditForm}
      employee={employee}
      is_company_pretax_enabled={is_company_pretax_enabled}
      mailing_address_description_placeholder={
        mailing_address_description_placeholder
      }
      setShowAddressMessageModal={setShowAddressMessageModal}
      setShowPhoneNumberModal={setShowPhoneNumberModal}
      setShowEditForm={setShowEditForm}
      onAvatarUpload={onAvatarUpload}
      onEmployeeSubmit={handleEmployeeSubmit}
    />
  );
};

export default SettingsProfilePage;
